import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useUiLang } from "../../../../stores/uiLang";

import canvasKeys from "../../../../assets/images/canvas/canvasKeys.png";
import ltiAdvantages from "../../../../assets/images/canvas/ltiAdvantages.png";
import placements from "../../../../assets/images/canvas/placements.png";
import selectAPP from "../../../../assets/images/canvas/selectAPP.png";
import addApp from "../../../../assets/images/canvas/addApp.png";
import assignmentTab from "../../../../assets/images/canvas/assignmentTab.png";
import findAssignmentTool from "../../../../assets/images/canvas/findAssignmentTool.png";
import selectTool from "../../../../assets/images/canvas/selectTool.png";
import addModule from "../../../../assets/images/canvas/addModule.png";
import publishModule from "../../../../assets/images/canvas/publishModule.png";
import addToModule from "../../../../assets/images/canvas/addToModule.png";
import addAssignmentOrExternalTool from "../../../../assets/images/canvas/addAssignmentOrExternalTool.png";
import addingExternal from "../../../../assets/images/canvas/addingExternal.png";
import addAssignment from "../../../../assets/images/canvas/addAssignment.png";

export default function LtiRegistrationGuide() {
  // Run E2 J2 round 3: guide in English and Spanish (shared UI language).
  // Canvas menu names stay as they appear in Canvas.
  const es = useUiLang() === "es";
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  const handleImageClick = (imageSrc: string) => {
    setZoomedImage(imageSrc);
  };

  const closeZoom = () => {
    setZoomedImage(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && zoomedImage) {
        closeZoom();
      }
    };

    if (zoomedImage) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [zoomedImage]);
  return (
    <div className="bg-zinc-50 pt-20 min-h-screen">
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-2"
          onClick={closeZoom}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={closeZoom}
              className="absolute top-1 right-1 text-white text-2xl font-light bg-red-600 hover:bg-red-700 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 shadow-lg z-10"
            >
              ✕
            </button>
            <img
              src={zoomedImage}
              alt={es ? "Vista ampliada" : "Zoomed view"}
              className="max-w-[95vw] max-h-[95vh] w-auto h-auto rounded-lg shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-16 text-white text-center px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-4">{es ? "Guía de registro LTI" : "LTI Registration Guide"}</h1>
          <p className="text-lg max-w-2xl mx-auto">
            {es ? "Aprende a conectar Project Explore con tu sistema de gestión del aprendizaje (LMS) por medio de LTI 1.3." : "Learn how to integrate Project Explore with your Learning Management System (LMS) using LTI 1.3."}
          </p>
        </div>
      </section>

      <section className="py-4 px-6 max-w-4xl mx-auto">
        <nav className="text-sm text-gray-600">
          <Link to="/support/help-center" className="hover:text-indigo-600">
            {es ? "Centro de ayuda" : "Help Center"}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">{es ? "Guía de registro LTI" : "LTI Registration Guide"}</span>
        </nav>
      </section>

      <section className="py-8 px-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{es ? "Resumen" : "Overview"}</h2>
            <p className="text-gray-700 mb-4">
              {es ? "LTI (Learning Tools Interoperability) te permite conectar Project Explore con el LMS que ya usas. Esta guía explica cómo hacer el registro desde nuestro panel de administración." : "LTI (Learning Tools Interoperability) allows you to seamlessly integrate Project Explore into your existing LMS. This guide will walk you through the registration process using our admin dashboard."}
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <p className="text-blue-800">
                <strong>{es ? "Nota:" : "Note:"}</strong>{" "}
                {es ? "Necesitas acceso de administrador para registrar plataformas LTI." : "You'll need administrator access to register LTI platforms."}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {es ? "Guía de configuración de Canvas" : "Canvas Setup Guide"}
            </h2>

            <div className="space-y-8">
              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {es ? "Paso 1: Registra la plataforma en el panel de administración" : "Step 1: Register Platform in Admin Dashboard"}
                </h3>
                <p className="text-gray-700 mb-4">
                  {es ? "Primero, registra tu plataforma Canvas en el panel de administración de Project Explore:" : "First, register your Canvas platform in the Project Explore admin dashboard:"}
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                  <li>{es ? "Ve al panel de administración" : "Navigate to the admin dashboard"}</li>
                  <li>{es ? "Abre la sección LTI Platforms" : "Go to the LTI section"}</li>
                  <li>{es ? "Haz clic en \"Register New Platform\"" : "Click \"Register New Platform\""}</li>
                  <li>{es ? "Llena los datos de configuración de Canvas (ver abajo)" : "Fill in the Canvas configuration details (see below)"}</li>
                  <li>{es ? "Guarda el registro de la plataforma" : "Save the platform registration"}</li>
                </ol>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {es ? "Paso 2: Crea una clave de desarrollador en Canvas" : "Step 2: Create Canvas Developer Key"}
                </h3>
                <p className="text-gray-700 mb-4">
                  {es ? "En Canvas, crea una clave de desarrollador nueva para Project Explore:" : "In Canvas, create a new developer key for Project Explore:"}
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    {es ? "Ve a " : "Go to "}<strong>Admin</strong> →{" "}
                    <strong>Developer Keys</strong>
                  </li>
                  <li>
                    {es ? "Haz clic en " : "Click "}<strong>+ Developer Key</strong> →{" "}
                    <strong>+ LTI Key</strong>
                  </li>
                  <li>{es ? "Llena la sección Key Settings:" : "Fill in the Key Settings:"}</li>
                </ol>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {es ? "Key Settings (configuración de la clave)" : "Key Settings"}
                  </h4>
                  <div className="space-y-2 text-sm [overflow-wrap:anywhere]">
                    <div>
                      <p>
                        <strong>Key Name:</strong> Project Explore
                      </p>
                      <p>
                        <strong>Owner Email:</strong> {es ? "[Tu correo electrónico]" : "[Your email]"}
                      </p>
                      <p>
                        <strong>Redirect URIs:</strong>{" "}
                        https://pe.startupsmartup.com/api/v1/lti
                      </p>
                    </div>
                  </div>

                  <h4 className="font-semibold text-gray-900 mb-3 mt-4">
                    {es ? "Sección Configure" : "Configure Section"}
                  </h4>
                  <div className="space-y-2 text-sm [overflow-wrap:anywhere]">
                    <div>
                      <p>
                        <strong>Method:</strong> Manual Entry
                      </p>
                      <p>
                        <strong>Title:</strong> Project Explore
                      </p>
                      <p>
                        <strong>Description:</strong> Project Explore
                      </p>
                      <p>
                        <strong>Target Link URI:</strong>{" "}
                        https://pe.startupsmartup.com/api/v1/lti
                      </p>
                      <p>
                        <strong>JWK Method:</strong> Public JWK URL
                      </p>
                      <p>
                        <strong>Public JWK URL:</strong>{" "}
                        https://pe.startupsmartup.com/api/v1/lti/keys
                      </p>
                      <p>
                        <strong>OpenID Connect Initiation URL:</strong>{" "}
                        https://pe.startupsmartup.com/api/v1/lti/login
                      </p>
                      <p>
                        <strong>LTI Advantage Service:</strong>{" "}
                        {es ? "activa las opciones disponibles" : "Enable the options available"}
                      </p>
                      <p>
                        <strong>Add to placements:</strong>{" "}
                        {es ? 'Link Selection y Assignment Selection, con el tipo de mensaje "LtiDeepLinkingRequest"' : 'Link Selection and Assignment Selection and set message type to "LtiDeepLinkingRequest"'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <img
                    src={canvasKeys}
                    alt="Canvas Developer Key Configuration"
                    className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                    onClick={() => handleImageClick(canvasKeys)}
                  />
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    {es ? "Pantalla de configuración de la clave de desarrollador en Canvas" : "Canvas Developer Key Configuration Screen"}
                  </p>
                </div>
                <div className="mt-6">
                  <img
                    src={ltiAdvantages}
                    alt="LTI Advantages"
                    className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                    onClick={() => handleImageClick(ltiAdvantages)}
                  />
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    {es ? "Opciones de LTI Advantage" : "LTI Advantages"}
                  </p>
                </div>
                <div className="mt-6">
                  <img
                    src={placements}
                    alt="LTI Placements"
                    className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                    onClick={() => handleImageClick(placements)}
                  />
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    {es ? "Ubicaciones (placements) de LTI" : "LTI Placements"}
                  </p>
                </div>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {es ? "Paso 3: Agrega la app de Project Explore a tu curso" : "Step 3: Add Project Explore App to Your Course"}
                </h3>
                <p className="text-gray-700 mb-4">
                  {es ? "Cuando la clave de desarrollador esté creada, agrega Project Explore a tu curso:" : "Once the developer key is created, add Project Explore to your course:"}
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                  <li>{es ? "Ve a tu curso de Canvas" : "Go to your Canvas course"}</li>
                  <li>
                    {es ? "Ve a " : "Navigate to "}<strong>Settings</strong> →{" "}
                    <strong>Apps</strong>
                  </li>
                  <li>{es ? "Busca \"Project Explore\" en la lista de apps disponibles" : "Find \"Project Explore\" in the list of available apps"}</li>
                  <li>
                    {es ? "Haz clic en " : "Click "}<strong>Add App</strong>
                  </li>
                  <li>{es ? "Completa cualquier otra configuración que se te pida" : "Configure any additional settings if prompted"}</li>
                </ol>

                <div className="mt-6 space-y-4">
                  <div>
                    <img
                      src={selectAPP}
                      alt="Select Project Explore App"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(selectAPP)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Elige Project Explore en la lista de apps" : "Select Project Explore from the Apps list"}
                    </p>
                  </div>

                  <div>
                    <img
                      src={addApp}
                      alt="Add App Configuration"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(addApp)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Configura y agrega la app de Project Explore" : "Configure and add the Project Explore app"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {es ? "Paso 4: Crea una tarea de Project Explore" : "Step 4: Create Project Explore Assignment"}
                </h3>
                <p className="text-gray-700 mb-4">
                  {es ? "Ahora puedes crear tareas con Project Explore:" : "Now you can create assignments using Project Explore:"}
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    {es ? "Ve a " : "Go to "}<strong>Assignments</strong>{es ? " en tu curso" : " in your course"}
                  </li>
                  <li>
                    {es ? "Haz clic en " : "Click "}<strong>+ Assignment</strong>
                  </li>
                  <li>
                    {es ? "En el tipo de entrega, elige" : "In the submission type, select"}{" "}
                    <strong>External Tool</strong>
                  </li>
                  <li>{es ? "Elige \"Project Explore\" en la lista" : "Choose \"Project Explore\" from the list"}</li>
                  <li>{es ? "Configura la tarea" : "Configure the assignment settings"}</li>
                  <li>{es ? "Guarda y publica la tarea" : "Save and publish the assignment"}</li>
                </ol>

                <div className="mt-6 space-y-4">
                  <div>
                    <img
                      src={assignmentTab}
                      alt="Canvas Assignments Tab"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(assignmentTab)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Ve a la pestaña Assignments" : "Navigate to the Assignments tab"}
                    </p>
                  </div>

                  <div>
                    <img
                      src={findAssignmentTool}
                      alt="Find Assignment Tool"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(findAssignmentTool)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Busca la herramienta de tareas de Project Explore" : "Find the Project Explore assignment tool"}
                    </p>
                  </div>

                  <div>
                    <img
                      src={selectTool}
                      alt="Select External Tool"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(selectTool)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Elige Project Explore en la lista de herramientas externas" : "Select Project Explore from the external tools list"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {es ? "Paso 5: Organiza en módulos" : "Step 5: Organize in Modules"}
                </h3>
                <p className="text-gray-700 mb-4">
                  {es ? "Puedes organizar el contenido de Project Explore en módulos para ordenar mejor tu curso:" : "You can organize your Project Explore content in modules for better course structure:"}
                </p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    {es ? "Ve a " : "Go to "}<strong>Modules</strong>{es ? " en tu curso" : " in your course"}
                  </li>
                  <li>
                    {es ? "Haz clic en " : "Click "}<strong>+ Module</strong>{es ? " para crear un módulo nuevo" : " to create a new module"}
                  </li>
                  <li>{es ? "Publica el módulo para que los estudiantes lo vean" : "Publish the module to make it available to students"}</li>
                  <li>{es ? "Agrega el contenido o las tareas al módulo" : "Add the content or assignments to the module"}</li>
                </ol>

                <div className="mt-6 space-y-4">
                  <div>
                    <img
                      src={addModule}
                      alt="Add Module"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(addModule)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Crea un módulo nuevo para tu contenido" : "Create a new module for your content"}
                    </p>
                  </div>
                  <div>
                    <img
                      src={publishModule}
                      alt="Publish Module"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(publishModule)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Publica el módulo" : "Publish the module"}
                    </p>
                  </div>

                  <div>
                    <img
                      src={addToModule}
                      alt="Add Assignment to Module"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(addToModule)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Agrega al módulo tu contenido o tus tareas de Project Explore" : "Add your Project Explore content or assignments to the module"}
                    </p>
                  </div>

                  <div>
                    <img
                      src={addAssignmentOrExternalTool}
                      alt="Add Assignment or External Tool"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() =>
                        handleImageClick(addAssignmentOrExternalTool)
                      }
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Elige una herramienta externa o una tarea para agregar" : "Select external tool or assignment to add"}
                    </p>
                  </div>

                  <div>
                    <img
                      src={addingExternal}
                      alt="Adding External Tool"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(addingExternal)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Agrega contenido al módulo con la herramienta externa" : "Add content to module using the external tool"}
                    </p>
                  </div>
                  <div>
                    <img
                      src={addAssignment}
                      alt="Add Assignment"
                      className="w-full max-w-4xl rounded-lg border border-gray-200 shadow-sm cursor-zoom-in hover:shadow-lg transition-shadow"
                      onClick={() => handleImageClick(addAssignment)}
                    />
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {es ? "Agrega una tarea al módulo eligiendo entre las tareas que ya existen" : "Add assignment to module by selecting from existing assignments"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {es ? "Campos obligatorios del registro" : "Required Registration Fields"}
            </h2>
            <p className="text-gray-700 mb-4">{es ? "Ejemplo de registro en Canvas LMS:" : "Example register canvas lms:"}</p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Platform URL *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "El dominio de tu Canvas" : "Your Canvas domain"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono break-all">
                    https://canvas.instructure.com
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Platform Name *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "Un nombre que describa tu plataforma" : "A descriptive name for your platform"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono">
                    Canvas LMS
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Client ID *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "El identificador de cliente (Client ID) de Canvas" : "The client identifier from Canvas"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono">
                    1000000001
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Authentication Endpoint *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "Punto de autorización OAuth 2.0" : "OAuth 2.0 authorization endpoint"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono break-all">
                    https://your-canvas-domain/api/lti/authorize_redirect
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Access Token Endpoint *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "Punto de token OAuth 2.0" : "OAuth 2.0 token endpoint"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono break-all">
                    https://your-canvas-domain/login/oauth2/token
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Auth Method *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "Método de autenticación" : "Authentication method"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono">
                    JWK Set
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Keyset URL *
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {es ? "URL del conjunto de claves JSON Web Key Set (JWKS)" : "URL to the JSON Web Key Set (JWKS)"}
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono break-all">
                    https://your-canvas-domain/api/lti/security/jwks
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {es ? "Otras plataformas LMS" : "Other LMS Platforms"}
            </h2>

            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {es ? "Plataforma LMS general" : "General LMS Platform"}
              </h3>
              <div className="space-y-2 text-sm [overflow-wrap:anywhere]">
                <div>
                  <p>
                    <strong>Platform URL:</strong> https://your-lms-domain.com
                  </p>
                  <p>
                    <strong>Auth Endpoint:</strong>{" "}
                    https://your-lms-domain.com/oauth2/auth
                  </p>
                  <p>
                    <strong>Token Endpoint:</strong>{" "}
                    https://your-lms-domain.com/oauth2/token
                  </p>
                  <p>
                    <strong>Keyset URL:</strong>{" "}
                    https://your-lms-domain.com/.well-known/jwks.json
                  </p>
                  <p>
                    <strong>Auth Method:</strong> JWK Set
                  </p>
                  <p>
                    <strong>Client ID:</strong>{" "}
                    {es ? "se configura en tu LMS" : "Configure in your LMS settings"}
                  </p>
                  <p>
                    <strong>{es ? "Nota:" : "Note:"}</strong>{" "}
                    {es ? "pide al administrador de tu LMS las URL de los puntos de conexión." : "Contact your LMS administrator for specific endpoint URLs"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {es ? "Solución de problemas" : "Troubleshooting"}
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-yellow-400 pl-4">
                <h4 className="font-semibold text-gray-900">{es ? "Problemas comunes" : "Common Issues"}</h4>
                <ul className="text-gray-700 mt-2 space-y-1">
                  <li>
                    • <strong>{es ? "Falló la autenticación" : "Authentication Failed"}:</strong>{" "}
                    {es ? "Revisa que el Client ID y los puntos de conexión sean correctos" : "Verify your Client ID and endpoints are correct"}
                  </li>
                  <li>
                    • <strong>{es ? "Formato de URL no válido" : "Invalid URL Format"}:</strong>{" "}
                    {es ? "Revisa que todas las URL incluyan el protocolo (https://)" : "Ensure all URLs include the protocol (https://)"}
                  </li>
                  <li>
                    • <strong>{es ? "La URL del conjunto de claves no está accesible" : "Keyset URL Not Accessible"}:</strong>{" "}
                    {es ? "Revisa que el punto JWKS sea público" : "Verify the JWKS endpoint is publicly accessible"}
                  </li>
                  <li>
                    • <strong>{es ? "La plataforma ya está registrada" : "Platform Already Registered"}:</strong>{" "}
                    {es ? "Revisa si la plataforma ya está configurada" : "Check if the platform is already configured"}
                  </li>
                  <li>
                    • <strong>{es ? "La app no aparece en el curso" : "App Not Showing in Course"}:</strong>{" "}
                    {es ? "Revisa que la clave de desarrollador esté activada y que la app esté agregada al curso" : "Make sure the developer key is enabled and the app is added to the course"}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">{es ? "¿Necesitas ayuda?" : "Need Help?"}</h2>
            <p className="text-gray-700 mb-4">
              {es ? "Si tienes algún problema con el registro LTI, nuestro equipo de ayuda te puede apoyar." : "If you encounter any issues during the LTI registration process, our support team is here to help."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:hello@startupsmartup.com"
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium text-center hover:bg-indigo-700 transition"
              >
                {es ? "Contactar a soporte" : "Contact Support"}
              </a>
              <Link
                to="/support/help-center"
                className="border border-indigo-600 text-indigo-600 px-6 py-2 rounded-lg font-medium text-center hover:bg-indigo-50 transition"
              >
                {es ? "Volver al Centro de ayuda" : "Back to Help Center"}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
